import React, { useState, useEffect } from "react";
import {
  Chip,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { getJson } from "../../../utils/api";
import { AdminMissedQuestionItem } from "../../../models";
import { ADMIN } from "../utils";

const QuestionsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missedQuestions, setMissedQuestions] = useState<
    AdminMissedQuestionItem[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await getJson(
          `${ADMIN}/learning/metrics/missed-questions?limit=50`
        );
        const { data } = await resp.json();
        if (!cancelled) setMissedQuestions(data?.questions ?? []);
      } catch (e) {
        console.error("Failed to fetch missed questions", e);
        if (!cancelled) setError("Failed to load missed questions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) return <Typography color="error">{error}</Typography>;
  if (loading) return <Typography>Loading...</Typography>;

  return (
    <>
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Most Missed Questions (Global)
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Question</TableCell>
              <TableCell>Lesson</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Type</TableCell>
              <Tooltip
                title="Total number of first attempts on this question across all users"
                arrow
                placement="top"
              >
                <TableCell align="right">First Attempts</TableCell>
              </Tooltip>
              <Tooltip
                title="Number of incorrect first attempts"
                arrow
                placement="top"
              >
                <TableCell align="right">Incorrect</TableCell>
              </Tooltip>
              <Tooltip title="Incorrect / First Attempts" arrow placement="top">
                <TableCell align="right">Miss Rate</TableCell>
              </Tooltip>
              <TableCell align="center">CMS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {missedQuestions.map((q, idx) => (
              <TableRow key={`${q.lesson_id}-${q.node_id}-${idx}`}>
                <TableCell sx={{ maxWidth: 400 }}>{q.question_text}</TableCell>
                <TableCell>
                  {q.lesson_id ? (
                    <MuiLink
                      component={Link}
                      to={`/lessons/${q.lesson_id}/edit`}
                      underline="hover"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {q.lesson_title}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </MuiLink>
                  ) : (
                    q.lesson_title
                  )}
                </TableCell>
                <TableCell>
                  {q.curriculum_id ? (
                    <MuiLink
                      component={Link}
                      to={`/curricula/${q.curriculum_id}`}
                      underline="hover"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {q.unit_title ?? "--"}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </MuiLink>
                  ) : (
                    q.unit_title ?? "--"
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={q.question_type}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">{q.total_first_attempts}</TableCell>
                <TableCell align="right">
                  {q.incorrect_first_attempts}
                </TableCell>
                <TableCell align="right">{q.miss_rate.toFixed(1)}%</TableCell>
                <TableCell align="center">
                  <MuiLink
                    component={Link}
                    to={`/lessons/${q.lesson_id}/edit`}
                    underline="hover"
                  >
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </MuiLink>
                </TableCell>
              </TableRow>
            ))}
            {missedQuestions.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No data
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default React.memo(QuestionsTab);
